import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import StatusPill from './StatusPill';

// Header project selector — replaces the old Projects page. Picking a project
// here sets the active project for all project-scoped tabs. It does not edit
// anything; editing lives on the Project Details tab.
function ProjectSelector({ projects, loading, selectedCode, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  const selected = useMemo(
    () => projects.find((p) => p.projectCode === selectedCode) || null,
    [projects, selectedCode]
  );

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (!open) setQuery('');
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      p.projectCode?.toLowerCase().includes(q) ||
      p.name?.toLowerCase().includes(q));
  }, [projects, query]);

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Select project"
        disabled={loading}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          background: 'transparent', border: '1px solid var(--brio-border, #ccc)',
          borderRadius: 6, cursor: loading ? 'wait' : 'pointer', padding: '0.35rem 0.6rem',
          fontFamily: 'Raleway, sans-serif',
        }}
      >
        {selected ? (
          <>
            <code style={{ fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>{selected.projectCode}</code>
            <strong style={{ fontSize: '0.9rem' }}>{selected.name}</strong>
            <StatusPill status={selected.briotimeStatus} />
          </>
        ) : (
          <span style={{ fontSize: '0.9rem', color: 'var(--brio-text-muted)' }}>
            {loading ? 'Loading projects…' : 'Select project'}
          </span>
        )}
        <ChevronDown size={14} style={{ color: 'var(--brio-text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 900 }} />
          <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 1000, background: '#fff', border: '1px solid var(--brio-border, #ccc)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', borderRadius: 6, padding: '0.5rem', width: 340, fontFamily: 'Raleway, sans-serif' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', border: '1px solid var(--brio-border-subtle, #e5e5e5)', borderRadius: 6, padding: '0.3rem 0.5rem', marginBottom: '0.4rem' }}>
              <Search size={14} style={{ color: 'var(--brio-text-muted)' }} />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search code or name…"
                style={{ border: 'none', outline: 'none', flex: 1, fontSize: '0.85rem', fontFamily: 'Raleway, sans-serif', background: 'transparent' }}
              />
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {filtered.length === 0 ? (
                <div style={{ padding: '0.6rem 0.5rem', fontSize: '0.85rem', color: 'var(--brio-text-muted)' }}>
                  No projects match.
                </div>
              ) : filtered.map((p) => {
                const active = p.projectCode === selectedCode;
                return (
                  <button
                    key={p.projectCode}
                    type="button"
                    onClick={() => { setOpen(false); onSelect(p.projectCode); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', textAlign: 'left',
                      padding: '0.4rem 0.5rem', border: 'none', borderRadius: 4, cursor: 'pointer',
                      background: active ? 'var(--brio-bg, #f7f7f5)' : 'transparent', fontFamily: 'Raleway, sans-serif',
                    }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--brio-bg, #f7f7f5)'; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <code style={{ fontSize: '0.75rem', color: 'var(--brio-text-muted)', minWidth: 34 }}>{p.projectCode}</code>
                    <span style={{ flex: 1, fontSize: '0.85rem' }}>{p.name}</span>
                    {p.hasMirror && <span style={{ fontSize: '0.62rem', fontWeight: 700, background: 'var(--brio-gold, #9F902A)', color: '#fff', borderRadius: 3, padding: '0 4px' }}>BOQ</span>}
                    <StatusPill status={p.briotimeStatus} />
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ProjectSelector;
